import { useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { Sphere, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

export const getGradientColors = (value) => {
  // Clamp the value between -1 and 1
  const clampedValue = Math.max(-1, Math.min(1, value));

  if (clampedValue > 0) {
    if (clampedValue <= 0.05) {
      // Low Positive (0%-5%)
      return {
        colorStart: '#99f6e4', // Light green/mint
        colorEnd: '#2dd4bf'    // Medium green
      };
    } else if (clampedValue <= 0.15) {
      // Medium Positive (5%-15%)
      return {
        colorStart: '#2dd4bf', // Medium green
        colorEnd: '#34d399'    // Bright green
      };
    } else {
      // High Positive (>15%)
      return {
        colorStart: '#34d399', // Bright green
        colorEnd: '#22c55e'    // Pure green
      };
    }
  } else if (clampedValue < 0) {
    if (clampedValue >= -0.05) {
      // Low Negative (-5% to 0%)
      return {
        colorStart: '#fda4af', // Light red/pink
        colorEnd: '#fb7185'    // Medium red/pink
      };
    } else if (clampedValue >= -0.15) {
      // Medium Negative (-15% to -5%)
      return {
        colorStart: '#fb7185', // Medium red/pink
        colorEnd: '#e11d48'    // Dark red
      };
    } else {
      // High Negative (<-15%)
      return {
        colorStart: '#e11d48', // Dark red
        colorEnd: '#CC0033'    // Deep red
      };
    }
  } else {
    // Neutral value (0%)
    return {
      colorStart: '#64748B', // Slate 500
      colorEnd: '#475569'    // Slate 600
    };
  }
};
const generateHeatmapTexture = (data, tileCount) => {
  const canvas = document.createElement('canvas');
  canvas.width = 3096;
  canvas.height = 3096;

  const context = canvas.getContext('2d');

  // Define the size of each tile (taller height)
  const tileWidth = canvas.width / tileCount;
  const tileHeight = tileWidth * 1.25; // Adjust height to be taller than width

  // Set font sizes for ticker symbols and percentages
  const tickerFontSize = tileWidth * 0.25;
  const percentFontSize = tileWidth * 0.2;

  // Global shadow settings for better text readability
  context.shadowColor = 'rgba(0, 0, 0, 0.7)';
  context.shadowBlur = 3;
  context.shadowOffsetX = 1;
  context.shadowOffsetY = 1;

  // Draw each tile with gradient color, ticker symbol, and percentage
  data.forEach((point, index) => {
    const x = (index % tileCount) * tileWidth;
    const y = Math.floor(index / tileCount) * tileHeight;

    // Get gradient colors based on value
    const { colorStart, colorEnd } = getGradientColors(point.value);

    // Create a gradient for the tile background
    const gradient = context.createLinearGradient(x, y, x + tileWidth, y + tileHeight);
    gradient.addColorStop(0, colorStart);
    gradient.addColorStop(1, colorEnd);

    // Fill tile with gradient
    context.fillStyle = gradient;
    context.fillRect(x, y, tileWidth, tileHeight);

    // Add borders to tiles for better separation
    context.strokeStyle = '#ffffff33'; // Semi-transparent white
    context.lineWidth = 1;
    context.strokeRect(x, y, tileWidth, tileHeight);

    // Set text style
    context.fillStyle = '#ffffff';
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Draw the ticker symbol
    context.font = `${tickerFontSize}px Roboto, sans-serif`;
    context.fillText(point.ticker?.toUpperCase(), x + tileWidth / 2, y + tileHeight / 2 - percentFontSize / 2);

    // Draw the percentage below the ticker symbol
    context.font = `bold ${percentFontSize}px Roboto, sans-serif`;
    const percentText = `${(point.value * 100).toFixed(2)}%`;
    context.fillText(percentText, x + tileWidth / 2, y + tileHeight / 2 + tickerFontSize / 2);
  });

  return new THREE.CanvasTexture(canvas);
};

const legendItems = [
  { label: 'High Positive (>15%)', colorStart: '#00FF99', colorEnd: '#00FF00' },
  { label: 'Medium Positive (5%-15%)', colorStart: '#6ee7b7', colorEnd: '#00FF99' },
  { label: 'Low Positive (0%-5%)', colorStart: '#ecfdf5', colorEnd: '#6ee7b7' },
  { label: 'Low Negative (-5% to 0%)', colorStart: '#fda4af', colorEnd: '#fb7185' },
  { label: 'Medium Negative (-15%-5%)', colorStart: '#fb7185', colorEnd: '#e11d48' },
  { label: 'High Negative (<-15%)', colorStart: '#e11d48', colorEnd: '#CC0033' },
  { label: 'Neutral (0%)', colorStart: '#64748B', colorEnd: '#475569' },
];

const ThreeLegend = () => {
  return (
    <div style={styles.container}>
      <h3 style={styles.header}>Market Returns</h3>
      <ul style={styles.list}>
        {legendItems.map((item, index) => (
          <li key={index} style={styles.listItem}>
            <div
              style={{
                ...styles.colorBox,
                background: `linear-gradient(to right, ${item.colorStart}, ${item.colorEnd})`,
              }}
            ></div>
            <span>{item.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const styles = {
  container: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    color: '#fff',
    fontFamily: 'Roboto, sans-serif',
    background: 'rgba(0, 0, 0, 0.6)',
    padding: '15px',
    borderRadius: '8px',
    maxWidth: '250px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
  },
  header: {
    marginBottom: '15px',
    fontSize: '18px',
    textAlign: 'center',
  },
  list: {
    listStyleType: 'none',
    padding: 0,
    margin: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  colorBox: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    marginRight: '10px',
    boxShadow: '0 0 2px rgba(0,0,0,0.5)',
  },
};

const MarketSphere = ({ heatmapTexture }) => {
  return (
    <Sphere args={[2, 64, 64]}>
      <meshStandardMaterial
        map={heatmapTexture}
        transparent={false}
        opacity={1}
        roughness={0.2}
        metalness={0.05}
      />
    </Sphere>
  );
};

const HeatmapGlobe3d = ({ data, tileCount = 10 }) => {
  // Generate the heatmap texture based on data and tile count
  const heatmapTexture = useMemo(() => generateHeatmapTexture(data, tileCount), [data, tileCount]);

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', background: '#0d0d0d' }}>
      <Canvas camera={{ position: [3, 0, 5] }}>
        {/* Lighting */}
        <ambientLight intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <directionalLight position={[-5, 5, 5]} intensity={0.5} />

        {/* Controls and Sphere */}
        <OrbitControls enableZoom={true} />
        <MarketSphere heatmapTexture={heatmapTexture} />
      </Canvas>

      {/* Legend */}
      <ThreeLegend />
    </div>
  );
};

export default HeatmapGlobe3d;